// src/pages/OpsMonitor.jsx
//
// "Ops Monitor" — second tab on /admin/cron-health (global_admin only).
// Four sections in a 2x2 grid (column-first flow):
//   Left:  Active Alerts, Quality Failures
//   Right: Active Conversations, Today's Cost
// Each card sizes to its content and the page scrolls; a card whose table
// exceeds SECTION_MAX_H caps there and scrolls internally, so a long table
// never shrinks the other three into unreadable slivers.
// (Cron heartbeats live on the Cron Health tab; queue depth lives in CloudWatch.)
//
// TIMEZONE RULE: every timestamp rendered here is shown in America/New_York
// (EST/EDT). No UTC is ever displayed. All formatting goes through fmtET().
//
// Data: one global_admin-gated endpoint (GET /api/admin/ops-monitor/overview)
// via fetchWithCsrf; a 30s auto-refresh runs only while the toggle is ON.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, AlertTriangle, ShieldAlert, MessageSquare, DollarSign,
  RefreshCw, ExternalLink, CheckCircle, XCircle, ShieldCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchWithCsrf } from '../lib/fetchWithCsrf';

const PROVIDER_ORDER = ['anthropic', 'twilio', 'openai'];

// Short labels for the 7 onboarding pillars (QC strip chips).
const PILLAR_LABELS = {
  business_operations: 'Operations', industry_context: 'Industry', audience: 'Audience',
  qualification: 'Qualification', voice_guardrails: 'Voice', objections: 'Objections',
  conversion_goal: 'Conversion',
};

// ─── Time helpers — ALL display goes through America/New_York ───────────────────

function fmtET(iso, withSeconds = false) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    hour12: true, timeZoneName: 'short',
  }).format(d);
}

function money(n) {
  return `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Relative age label — timezone-independent (pure elapsed time).
function relTime(iso) {
  if (!iso) return '—';
  const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 0) return 'in the future';
  if (secs < 60) return `${secs}s ago`;
  const m = Math.round(secs / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

// Live "last updated" indicator — re-renders every second on its own, no fetch.
function LastUpdated({ iso }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!iso) return null;
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  return <>Last updated {secs}s ago</>;
}

// ─── Section card — sizes to content, caps + scrolls internally past the cap ────

// Max height a single card's scroll body may take before it scrolls internally
// (~14 table rows). Below this, the card just grows to show every row.
const SECTION_MAX_H = 'max-h-[32rem]';

function SectionCard({ title, icon: Icon, right, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
      <div className="flex-none flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        </div>
        {right}
      </div>
      <div className={`overflow-y-auto ${SECTION_MAX_H}`}>
        {children}
      </div>
    </div>
  );
}

// Tenant Pulse metric pill — muted label + bold value; gray when 0, else its tone.
function PulseMetric({ label, value, display, tone }) {
  const valueColor = value === 0
    ? 'text-gray-400'
    : tone === 'amber' ? 'text-amber-600'
    : tone === 'green' ? 'text-green-600'
    : 'text-indigo-600';
  return (
    <div className="flex items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</span>
      <span className={`text-xs font-bold tabular-nums ${valueColor}`}>{display ?? value}</span>
    </div>
  );
}

const thCls = 'text-left font-medium px-4 py-2.5';
const tdCls = 'px-4 py-2.5 align-top';
// Sticky header so column labels stay put while a section's rows scroll.
const theadCls = 'bg-gray-50 text-gray-500 text-xs uppercase tracking-wide sticky top-0 z-10';

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function OpsMonitor() {
  const [data, setData] = useState({
    tenantPulse: [],      // [{ tenant_id, name, sent_today, replies_today, hot_leads_today, cost_today, status }]
    alerts: [],
    failures: [],
    conversations: [],    // [{ tenant_id, midConversation, scheduling, hotNotHandedOff }]
    cost: [],             // [{ tenant_id, byProvider:{}, total }]
    tenants: {},          // id -> name
    campaigns: {},        // id -> name
    onboardingQc: null,   // { windowDays, total, reopened, reopenRate, skipped, pillarBounces, recent }
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  // Active Conversations inline expansion — keys are `${tenant_id}:${metricKey}`.
  const [expandedConvo, setExpandedConvo] = useState(() => new Set());
  const toggleConvo = (key) => setExpandedConvo((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const loadAll = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetchWithCsrf('/api/admin/ops-monitor/overview');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setData({
        tenantPulse: json.tenantPulse || [],
        alerts: json.alerts || [],
        failures: json.failures || [],
        conversations: json.conversations || [],
        cost: json.cost || [],
        tenants: json.tenants || {},
        campaigns: json.campaigns || {},
        onboardingQc: json.onboardingQc || null,
      });
      setUpdatedAt(new Date().toISOString());
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // One initial load on mount (so the tab shows data when opened).
  useEffect(() => { loadAll(); }, [loadAll]);

  // 30s auto-refresh — ONLY while toggled on. Created once per ON state,
  // torn down on OFF/unmount. Never recreated per render; no orphaned intervals.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => loadAll(true), 30000);
    return () => clearInterval(id);
  }, [autoRefresh, loadAll]);

  const tenantName = (id) => (id ? (data.tenants[id] || id.slice(0, 8)) : '—');

  const grandTotal = data.cost.reduce((s, t) => s + t.total, 0);
  const convoTotals = data.conversations.reduce(
    (acc, c) => ({
      midConversation: acc.midConversation + (c.midConversation?.count || 0),
      scheduling: acc.scheduling + (c.scheduling?.count || 0),
      hotNotHandedOff: acc.hotNotHandedOff + (c.hotNotHandedOff?.count || 0),
    }),
    { midConversation: 0, scheduling: 0, hotNotHandedOff: 0 }
  );

  // Tenant Pulse status dot: red if a problem (alert in 24h, or quality fail with
  // AI still off), green if backend says active, otherwise gray (idle/inactive).
  const pulseDot = (t) => {
    const problem =
      data.alerts.some((a) => a.tenant_id === t.tenant_id) ||
      data.failures.some((f) => f.tenant_id === t.tenant_id && f.lead?.ai_conversation_enabled === false);
    if (problem) return 'bg-red-500';
    return t.status === 'active' ? 'bg-green-500' : 'bg-gray-300';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col px-6 py-4 max-w-7xl mx-auto">
      {/* Header — pinned, never scrolls */}
      <div className="flex-none flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Activity className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ops Monitor</h1>
            <p className="text-sm text-gray-500">
              Alerts, quality kills, live conversations &amp; spend · all times ET
              {updatedAt && <> · <LastUpdated iso={updatedAt} /></>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border ${
              autoRefresh
                ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <button
            onClick={() => loadAll(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="flex-none mb-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Tenant Pulse — full-width SaaS status strip above the grid; hidden when none */}
      {data.tenantPulse.length > 0 && (
        <div className="flex-none mb-3 bg-white border border-gray-200 rounded-xl px-3 py-2">
          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Tenant Pulse</div>
          <div className="divide-y divide-gray-50">
            {data.tenantPulse.map((t) => (
              <div key={t.tenant_id} className="flex items-center gap-2 py-1.5 whitespace-nowrap">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${pulseDot(t)}`} />
                <span className="font-medium text-sm text-gray-800 w-48 truncate flex-shrink-0">{t.name || t.tenant_id.slice(0, 8)}</span>
                <div className="ml-auto flex items-center gap-2">
                  <PulseMetric label="Sent" value={t.sent_today} tone="indigo" />
                  <PulseMetric label="Replies" value={t.replies_today} tone="indigo" />
                  <PulseMetric label="Hot" value={t.hot_leads_today} tone="amber" />
                  <PulseMetric label="Cost" value={t.cost_today} display={money(t.cost_today)} tone="green" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Onboarding QC — full-width strip: is Surf's interviewing holding up, and
          which pillars does the judge most often have to reopen? Always shown (even
          at zero) so it's a visible, confirmable panel rather than a blank gap. */}
      {data.onboardingQc && (
        <div className="flex-none mb-3 bg-white border border-gray-200 rounded-xl px-3 py-2">
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-gray-400" />
            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              Onboarding QC · last {data.onboardingQc.windowDays}d
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <PulseMetric label="Onboardings" value={data.onboardingQc.total} tone="indigo" />
            <PulseMetric
              label="Reopen rate"
              value={data.onboardingQc.reopened}
              display={`${Math.round((data.onboardingQc.reopenRate || 0) * 100)}%`}
              tone="amber"
            />
            <PulseMetric label="Gate skipped" value={data.onboardingQc.skipped} tone="amber" />
            <span className="mx-1 h-4 w-px bg-gray-200" />
            <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Pillars reopened</span>
            {Object.keys(data.onboardingQc.pillarBounces || {}).length === 0 ? (
              <span className="text-xs text-gray-400">none</span>
            ) : (
              Object.entries(data.onboardingQc.pillarBounces)
                .sort((a, b) => b[1] - a[1])
                .map(([p, c]) => (
                  <span key={p} className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700">
                    {PILLAR_LABELS[p] || p} <span className="font-bold tabular-nums">{c}</span>
                  </span>
                ))
            )}
            {data.onboardingQc.total === 0 && (
              <span className="ml-auto text-[11px] text-gray-400 italic">awaiting first completed onboarding</span>
            )}
          </div>
        </div>
      )}

      {/* Two-column 2x2 grid — column-first flow so Left = Alerts→Failures,
          Right = Conversations→Cost. Rows size to content (items-start), so a
          short card never gets stretched to match a tall neighbor. */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 xl:grid-flow-col xl:[grid-template-rows:auto_auto] xl:items-start">

        {/* ── Active Alerts (col 1, row 1) ── */}
        <SectionCard
          title="Active Alerts"
          icon={AlertTriangle}
          right={<span className="text-xs text-gray-500">last 24h · {data.alerts.length}</span>}
        >
          <table className="w-full text-sm">
            <thead className={theadCls}>
              <tr>
                <th className={thCls}>Job</th>
                <th className={thCls}>Tenant</th>
                <th className={thCls}>Campaign</th>
                <th className={thCls}>Error type</th>
                <th className={thCls}>Alert key</th>
                <th className={thCls}>Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.alerts.map((a, i) => (
                <tr key={`${a.alert_key}-${i}`} className="hover:bg-gray-50">
                  <td className={`${tdCls} font-medium text-gray-900`}>{a.job || '—'}</td>
                  <td className={`${tdCls} text-gray-700`}>{a.tenant_id ? tenantName(a.tenant_id) : '—'}</td>
                  <td className={`${tdCls} text-gray-700`}>
                    {a.campaign_id ? (data.campaigns[a.campaign_id] || a.campaign_id.slice(0, 8)) : '—'}
                  </td>
                  <td className={`${tdCls} text-gray-700`}>{a.error_type || '—'}</td>
                  <td className={`${tdCls} text-gray-400 text-xs break-all`}>{a.alert_key}</td>
                  <td className={`${tdCls} text-gray-600 whitespace-nowrap`}>{fmtET(a.created_at, true)}</td>
                </tr>
              ))}
              {data.alerts.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No alerts in the last 24 hours.</td></tr>
              )}
            </tbody>
          </table>
        </SectionCard>

        {/* ── Quality Failures (col 1, row 2) ── */}
        <SectionCard
          title="Quality Failures"
          icon={ShieldAlert}
          right={<span className="text-xs text-gray-500">{data.failures.length} total</span>}
        >
          <table className="w-full text-sm">
            <thead className={theadCls}>
              <tr>
                <th className={thCls}>Tenant</th>
                <th className={thCls}>Lead</th>
                <th className={thCls}>Phone</th>
                <th className={thCls}>Rule</th>
                <th className={thCls}>Explanation</th>
                <th className={thCls}>Time</th>
                <th className={thCls}>AI status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.failures.map((f) => {
                const aiOff = f.lead?.ai_conversation_enabled === false;
                return (
                  <tr key={f.id} className={aiOff ? 'bg-red-50' : 'hover:bg-gray-50'}>
                    <td className={`${tdCls} text-gray-700`}>{tenantName(f.tenant_id)}</td>
                    <td className={tdCls}>
                      {f.lead_id ? (
                        <Link to={`/lead/${f.lead_id}`} className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium">
                          {f.lead?.name || 'Unknown'}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      ) : (f.lead?.name || 'Unknown')}
                    </td>
                    <td className={`${tdCls} text-gray-600 whitespace-nowrap`}>{f.lead?.phone || '—'}</td>
                    <td className={`${tdCls} font-medium text-gray-900`}>{f.rule}</td>
                    <td className={`${tdCls} text-gray-600 max-w-md whitespace-pre-wrap`}>{f.explanation}</td>
                    <td className={`${tdCls} text-gray-600 whitespace-nowrap`}>{fmtET(f.created_at, true)}</td>
                    <td className={tdCls}>
                      {f.lead == null ? (
                        <span className="text-xs text-gray-400">lead not found</span>
                      ) : aiOff ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700">
                          <XCircle className="w-3.5 h-3.5" /> AI OFF · action needed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                          <CheckCircle className="w-3.5 h-3.5" /> AI re-enabled
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {data.failures.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No quality failures recorded.</td></tr>
              )}
            </tbody>
          </table>
        </SectionCard>

        {/* ── Active Conversations (col 2, row 1) ── */}
        <SectionCard
          title="Active Conversations"
          icon={MessageSquare}
          right={<span className="text-xs text-gray-500">{data.conversations.length} tenants</span>}
        >
          <table className="w-full text-sm">
            <thead className={theadCls}>
              <tr>
                <th className={thCls}>Tenant</th>
                <th className={`${thCls} text-right`}>Mid-conversation</th>
                <th className={`${thCls} text-right`}>Scheduling</th>
                <th className={`${thCls} text-right`}>Hot · not handed off</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.conversations.map((c) => {
                const metrics = [
                  { key: 'midConversation', label: 'Mid-conversation' },
                  { key: 'scheduling', label: 'Scheduling' },
                  { key: 'hotNotHandedOff', label: 'Hot · not handed off', alert: true },
                ];
                return (
                  <React.Fragment key={c.tenant_id}>
                    <tr className="hover:bg-gray-50">
                      <td className={`${tdCls} text-gray-700`}>{tenantName(c.tenant_id)}</td>
                      {metrics.map((m) => {
                        const cnt = c[m.key].count;
                        const key = `${c.tenant_id}:${m.key}`;
                        const open = expandedConvo.has(key);
                        const hot = m.alert && cnt > 0;
                        return (
                          <td key={m.key} className={`${tdCls} text-right`}>
                            {cnt > 0 ? (
                              <button
                                onClick={() => toggleConvo(key)}
                                className={`font-semibold underline decoration-dotted underline-offset-2 hover:opacity-80 ${hot ? 'text-amber-700' : 'text-indigo-600'}`}
                              >
                                {cnt} {open ? '▲' : '▾'}
                              </button>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {metrics.map((m) => {
                      const key = `${c.tenant_id}:${m.key}`;
                      if (!expandedConvo.has(key)) return null;
                      return (
                        <tr key={key} className="bg-indigo-50/40">
                          <td colSpan={4} className="px-4 py-2">
                            <div className="text-xs font-semibold text-gray-500 mb-1.5">{m.label} · {c[m.key].count}</div>
                            <div className="space-y-1">
                              {c[m.key].leads.map((l) => (
                                <div key={l.id} className="flex items-center gap-3 text-xs">
                                  <Link to={`/lead/${l.id}`} className="inline-flex items-center gap-1 w-44 truncate text-indigo-600 hover:text-indigo-800 font-medium">
                                    <span className="truncate">{l.name || 'Unknown'}</span>
                                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                  </Link>
                                  <span className="w-32 text-gray-600 whitespace-nowrap">{l.phone || '—'}</span>
                                  <span className="w-28 text-gray-500 truncate">{l.status}</span>
                                  <span className="text-gray-400 whitespace-nowrap">{relTime(l.last_message_at)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {data.conversations.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No active conversations.</td></tr>
              )}
            </tbody>
            {data.conversations.length > 0 && (
              <tfoot className="border-t-2 border-gray-200 bg-gray-50 sticky bottom-0">
                <tr>
                  <td className={`${tdCls} font-semibold text-gray-700`}>All tenants</td>
                  <td className={`${tdCls} text-right font-semibold text-gray-700`}>{convoTotals.midConversation}</td>
                  <td className={`${tdCls} text-right font-semibold text-gray-700`}>{convoTotals.scheduling}</td>
                  <td className={`${tdCls} text-right font-bold ${convoTotals.hotNotHandedOff > 0 ? 'text-amber-700' : 'text-gray-700'}`}>{convoTotals.hotNotHandedOff}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </SectionCard>

        {/* ── Today's Cost (col 2, row 2) ── */}
        <SectionCard
          title="Today's Cost"
          icon={DollarSign}
          right={<span className="text-xs text-gray-500">since midnight ET · {money(grandTotal)}</span>}
        >
          <table className="w-full text-sm">
            <thead className={theadCls}>
              <tr>
                <th className={thCls}>Tenant</th>
                {PROVIDER_ORDER.map((p) => <th key={p} className={`${thCls} text-right`}>{p}</th>)}
                <th className={`${thCls} text-right`}>Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.cost.map((t) => (
                <tr key={t.tenant_id} className="hover:bg-gray-50">
                  <td className={`${tdCls} text-gray-700`}>{tenantName(t.tenant_id)}</td>
                  {PROVIDER_ORDER.map((p) => (
                    <td key={p} className={`${tdCls} text-right text-gray-600`}>
                      {t.byProvider[p] ? money(t.byProvider[p]) : '—'}
                    </td>
                  ))}
                  <td className={`${tdCls} text-right font-semibold text-gray-900`}>{money(t.total)}</td>
                </tr>
              ))}
              {data.cost.length === 0 && (
                <tr><td colSpan={PROVIDER_ORDER.length + 2} className="px-4 py-8 text-center text-gray-400">No cost events today.</td></tr>
              )}
            </tbody>
            {data.cost.length > 0 && (
              <tfoot className="border-t-2 border-gray-200 bg-gray-50 sticky bottom-0">
                <tr>
                  <td className={`${tdCls} font-semibold text-gray-700`}>All tenants</td>
                  {PROVIDER_ORDER.map((p) => {
                    const sum = data.cost.reduce((s, t) => s + (t.byProvider[p] || 0), 0);
                    return <td key={p} className={`${tdCls} text-right font-semibold text-gray-700`}>{sum ? money(sum) : '—'}</td>;
                  })}
                  <td className={`${tdCls} text-right font-bold text-gray-900`}>{money(grandTotal)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </SectionCard>

      </div>
    </div>
  );
}
